import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionBankPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    subject: '',
    exam_context: '',
    difficulty: ''
  });
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, [filters]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      // Use /questions/bank endpoint for item_pairs
      const response = await axios.get(`${API}/questions/bank`, {
        params: {
          subject: filters.subject || undefined,
          difficulty: filters.difficulty || undefined,
          exam_context: filters.exam_context || undefined
        },
        withCredentials: true
      });
      
      console.log('Questions fetched:', response.data);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Soru Bankası' : 'Question Bank'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('filters')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('discipline')}
              </label>
              <select
                value={filters.subject}
                onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('all')}</option>
                <option value="Physics">{t('physics')}</option>
                <option value="Mathematics">{t('mathematics')}</option>
                <option value="Chemistry">{t('chemistry')}</option>
                <option value="Biology">{t('biology')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('exam_type')}
              </label>
              <select
                value={filters.exam_context}
                onChange={(e) => setFilters({ ...filters, exam_context: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('all')}</option>
                <option value="AP">AP</option>
                <option value="TYT">TYT</option>
                <option value="AYT">AYT</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('difficulty')}
              </label>
              <select
                value={filters.difficulty}
                onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('all')}</option>
                <option value="easy">{t('easy')}</option>
                <option value="medium">{t('medium')}</option>
                <option value="hard">{t('hard')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Questions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('loading')}</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">
                {t('no_questions_found')}
              </p>
            </div>
          ) : (
            questions.map((question) => (
              <button
                key={question.id}
                onClick={() => setSelectedQuestion(question)}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 text-left"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {question.subject}
                  </span>
                  <span className="text-gray-500 text-sm">{question.difficulty}</span>
                </div>
                <p className="text-gray-800 font-medium mb-2 line-clamp-3">
                  {language === 'tr' ? (question.stem_TR || question.stem_EN) : (question.stem_EN || question.stem_TR)}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {question.exam_context?.map((ctx, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                      {ctx}
                    </span>
                  ))}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Question Detail Modal */}
      {selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedQuestion(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{selectedQuestion.subject}</h2>
              <button onClick={() => setSelectedQuestion(null)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('question')}:</h3>
                <p className="text-gray-800">
                  {language === 'tr' 
                    ? (selectedQuestion.stem_TR || selectedQuestion.stem_EN || 'Soru metni bulunamadı') 
                    : (selectedQuestion.stem_EN || selectedQuestion.stem_TR || 'Question text not found')
                  }
                </p>
              </div>
              {selectedQuestion.options?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">{t('options')}:</h3>
                  <ul className="space-y-2">
                    {selectedQuestion.options.map((option, idx) => (
                      <li key={idx} className="text-gray-700">{option}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('correct_answer')}:</h3>
                <p className="text-green-600 font-semibold">{selectedQuestion.correct_answer}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('solution')}:</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedQuestion.solution_full}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBankPage;